import { DecisionNodeData, NodeData, ServerNode } from 'pages/WorkflowBuilder/WorkflowBuilder.types';
import {
    createEdgesFromNodes,
    getEdgeStyle,
    getNodeConfigDataFromServer,
    mapNodeTypeToReactFlow,
    sanitizeWorkflowEdges,
} from 'pages/WorkflowBuilder/workflowNodeUtils';
import { buildDecisionNodeSubLabel } from 'pages/WorkflowBuilder/decisionNodeLabels';
import { useEffect } from 'react';
import { Edge, MarkerType, Node } from '@xyflow/react';

export const useInitialNodesEdges = (
    enabled: boolean,
    workflowDefinitionNodes: ServerNode[],
    setNodes: (nodes: Node<NodeData>[]) => void,
    setEdges: (edges: Edge<NodeData>[]) => void,
    deleteNode: (nodeId: string) => void
) => {
    useEffect(() => {
        if (!enabled || !workflowDefinitionNodes || workflowDefinitionNodes.length === 0) return;

        // Transform the server nodes format to ReactFlow format
        const reactFlowNodes: Node<NodeData>[] = workflowDefinitionNodes.map((node) => {
            // Extract the node config data
            const nodeConfigData = getNodeConfigDataFromServer(node);

            // For DELAY nodes, add the subLabel based on the extracted config
            let subLabel = '';
            if (node.type === 'DELAY') {
                if (node.config.delay_type === 'TIMESTAMP') {
                    subLabel = `At: ${new Date(node.config.delay?.timestamp || '').toLocaleString()}`;
                } else {
                    subLabel = `Wait: ${node.config.delay?.duration} ${node.config.delay?.unit?.toLowerCase() || 'minutes'}`;
                }
            } else if (node.type === 'CALL') {
                // Add a subLabel for call nodes showing the prompt name
                const promptName = node.config.promptName || node.config.aiPrompt || '';
                subLabel = `AI Prompt: ${promptName}`;
            } else if (node.type === 'DECISION') {
                subLabel = buildDecisionNodeSubLabel((nodeConfigData as Partial<DecisionNodeData>).conditions ?? []);
            }

            // Create the node with all required properties
            return {
                id: node.id,
                type: mapNodeTypeToReactFlow(node.type),
                position: node.uiConfig.position || { x: 0, y: 0 },
                data: {
                    label: node.uiConfig.label || node.type,
                    subLabel: subLabel,
                    ...nodeConfigData,
                    onDelete: deleteNode,
                },
            };
        });

        setNodes(reactFlowNodes);

        // Create edges based on the nextNode fields in node configs
        const derivedEdges = createEdgesFromNodes(workflowDefinitionNodes);

        // Add style and marker so arrows display (matches onConnect / onDecisionDone)
        const styledEdges = derivedEdges.map((edge) => ({
            ...edge,
            style: getEdgeStyle({ id: edge.id, selected: false }, null),
            selected: false,
            markerEnd: edge.markerEnd ?? { type: MarkerType.ArrowClosed },
        }));

        setEdges(sanitizeWorkflowEdges(styledEdges as Edge<NodeData>[], reactFlowNodes));
    }, [enabled, workflowDefinitionNodes]);
};
